/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useLocation } from 'js/lib/useRouter';

import { CardMetadata, type ProductCardProps, VisuallyHidden, breakpoints, useLocale } from '@coursera/cds-core';

import LearningProductCardColorCodedPillTags from 'bundles/product-card/components/LearningProductCardColorCodedPillTags';
import ProductCardRatings from 'bundles/product-card/components/ProductCardRatings';
import ProductCardReviews, { getProductCardReviewsAsString } from 'bundles/product-card/components/ProductCardReviews';
import StopPropagationWrapper from 'bundles/product-card/components/StopPropagationWrapper';

const styles = {
  buttons: css`
    margin-top: var(--cds-spacing-200);

    button.pathways-recommendation-coach {
      box-shadow: none;
    }

    ${breakpoints.down('sm')} {
      margin-top: var(--cds-spacing-150);

      > span {
        width: 100%;
        flex-direction: column-reverse;

        div.with-enroll-modal > button {
          width: 100%;
        }
      }
    }
  `,
  colorCodedPillTagsContainer: (productCardVariant: ProductCardProps['variant']) => css`
    width: stretch;
    margin-top: calc(var(--cds-spacing-50) + var(--cds-spacing-25));

    ${
      productCardVariant === 'grid' &&
      css`
        /* Need min-height for footer component alignment even when card has no pill tags. */
        min-height: 22px;
      `
    }
  `,
  cardMetadata: css`
    position: relative;
  `,
};

// Enterprise (org- or program-scoped) pages never render a pill tag on this footer.
const isEnterprisePathname = (pathname: string): boolean =>
  /^\/organizations\//.test(pathname) || /^\/programs\//.test(pathname);

type Props = {
  isMetadataAboveProductRating: boolean;
  cardMetadata: string[];
  avgProductRating?: number;
  numProductRatings?: number;
  badge?: React.ReactNode;
  shouldUseColorCodedPillTags: boolean;
  statusTagNames?: string[];
  buttons?: Array<React.ReactElement>;
  productCardVariant: ProductCardProps['variant'];
  badgeAfterMetadata?: boolean;
  topMatchLabel?: string;
};

const LearningProductCardFooter = ({
  isMetadataAboveProductRating,
  cardMetadata,
  avgProductRating,
  numProductRatings,
  badge,
  shouldUseColorCodedPillTags,
  statusTagNames,
  buttons,
  productCardVariant,
  badgeAfterMetadata = false,
  topMatchLabel,
}: Props) => {
  const locale = useLocale();
  const location = useLocation();
  const isEnterprisePage = isEnterprisePathname(location.pathname);

  const renderRating = () =>
    Boolean(avgProductRating) && Boolean(numProductRatings) ? (
      <ProductCardRatings avgProductRating={avgProductRating} numProductRatings={numProductRatings} />
    ) : null;

  const renderReviews = () => {
    if (shouldUseColorCodedPillTags) {
      return null;
    }
    return avgProductRating ? (
      <ProductCardReviews avgProductRating={avgProductRating} numProductRatings={numProductRatings} />
    ) : null;
  };

  const renderColorCodedPillTags = () => {
    return (
      <LearningProductCardColorCodedPillTags
        pillTags={statusTagNames?.map((tag) => ({ text: tag })) ?? []}
        topMatchLabel={topMatchLabel}
      />
    );
  };

  const buttonsWithStopPropagation = buttons?.map((button) => {
    return React.isValidElement(button) ? <StopPropagationWrapper>{button}</StopPropagationWrapper> : button;
  });

  const renderButtons = () =>
    buttonsWithStopPropagation && buttonsWithStopPropagation.length > 0 ? (
      <div css={styles.buttons}>{buttonsWithStopPropagation}</div>
    ) : null;

  const renderCardMetadata = () => {
    if (!shouldUseColorCodedPillTags) {
      return cardMetadata.length > 0 ? <CardMetadata metadata={cardMetadata} /> : null;
    }

    const reviewText = getProductCardReviewsAsString(locale, avgProductRating, numProductRatings);

    if (!reviewText) {
      return <CardMetadata metadata={cardMetadata} />;
    }

    const metadataToUse = [reviewText.reviewsString, ...cardMetadata];
    const ariaLabel = [reviewText.ariaLabel, ...cardMetadata].join(', ');

    // Use a real (visually-hidden) text node, the same pattern CDS's own Tag component uses for its status label.
    return (
      <span css={styles.cardMetadata}>
        <VisuallyHidden component="span">{ariaLabel}</VisuallyHidden>
        <span aria-hidden="true">
          <CardMetadata metadata={metadataToUse} />
        </span>
      </span>
    );
  };

  if (isMetadataAboveProductRating) {
    if (shouldUseColorCodedPillTags) {
      return renderCardMetadata();
    }
    return (
      <>
        {renderCardMetadata()}
        {renderRating()}
      </>
    );
  }

  if (shouldUseColorCodedPillTags) {
    return (
      <>
        {!badgeAfterMetadata && badge}
        {renderCardMetadata()}
        {!isEnterprisePage && (
          <div css={styles.colorCodedPillTagsContainer(productCardVariant)}>{renderColorCodedPillTags()}</div>
        )}
        {badgeAfterMetadata && badge}
        {renderButtons()}
      </>
    );
  }

  return (
    <>
      {!badgeAfterMetadata && badge}
      {renderReviews()}
      {renderCardMetadata()}
      {badgeAfterMetadata && badge}
      {renderButtons()}
    </>
  );
};

export default LearningProductCardFooter;
